import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { DateTime } from 'luxon';
import {
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaEdit,
  FaCheck,
  FaTimes,
  FaMapMarkerAlt,
  FaLock
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useAuthSync } from '../../hooks/useAuthSync';
import AppointmentService from '../../services/appointmentService';
import CalendarBlockService from '../../services/calendarBlockService';
import UserService from '../../services/userService';
import LocationService from '../../services/locationService';
import { getDateString, getTimeString } from '../../utils/dateUtils';
import './Appointments.css';

// Mexican official holidays 2026
const MEXICAN_HOLIDAYS_2026 = {
  '2026-01-01': 'Año Nuevo',
  '2026-02-02': 'Día de la Constitución',
  '2026-03-16': 'Natalicio de Benito Juárez',
  '2026-04-02': 'Jueves Santo',
  '2026-04-03': 'Viernes Santo',
  '2026-05-01': 'Día del Trabajo',
  '2026-09-16': 'Aniversario de la Independencia',
  '2026-11-16': 'Día de la Revolución Mexicana',
  '2026-12-25': 'Navidad'
};

const CALENDAR_SELECTED_DOCTORS_STORAGE_KEY = 'appointments:selectedDoctorIds';
const CALENDAR_SELECTED_LOCATIONS_STORAGE_KEY = 'appointments:selectedLocationIds';
const CALENDAR_SHOW_BLOCKS_STORAGE_KEY = 'appointments:showCalendarBlocks';

const getWeekStart = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // back to Sunday
  d.setHours(0, 0, 0, 0);
  return d;
};

const Appointments = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { timezone, isLoading: timezoneLoading } = useOrganizationTimezone();
  const { preferences: userPreferences } = useUserPreferences();
  const { hasPermission, user: authUser } = useAuthSync();
  const canViewAll = hasPermission('agenda:viewall');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState({});
  const [calendarBlocks, setCalendarBlocks] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('month');
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(new Date()));
  const loadedMonthsRef = useRef(new Set());
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState([]);
  const [doctorSelectionInitialized, setDoctorSelectionInitialized] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [locationSelectionInitialized, setLocationSelectionInitialized] = useState(false);
  const [showCalendarBlocks, setShowCalendarBlocks] = useState(false);
  const [calendarBlockVisibilityInitialized, setCalendarBlockVisibilityInitialized] = useState(false);
  const [hoveredHoliday, setHoveredHoliday] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const badgeRefs = useRef({});

  const selectedDoctorIdSet = useMemo(() => new Set(selectedDoctorIds), [selectedDoctorIds]);
  const selectedLocationIdSet = useMemo(() => new Set(selectedLocationIds), [selectedLocationIds]);


  // Load appointments — supports caching multiple months for week view
  useEffect(() => {
    if (!auth.user?.access_token || timezoneLoading) return;

    const loadMonth = async (year, month) => {
      const monthKey = `${year}-${month}`;
      if (loadedMonthsRef.current.has(monthKey)) return;
      loadedMonthsRef.current.add(monthKey);

      setIsLoading(true);
      setError(null);

      try {
        const appointmentsData = await AppointmentService.getAppointmentsByMonth(year, month, auth.user.access_token);

        const appointmentsByDate = {};
        (appointmentsData || []).forEach(appointment => {
          const appointmentDate = new Date(appointment.appointmentDateTime);
          const dateKey = getDateString(appointmentDate, timezone);
          if (!appointmentsByDate[dateKey]) appointmentsByDate[dateKey] = [];
          appointmentsByDate[dateKey].push({
            ...appointment,
            patient: appointment.patient,
            doctor: appointment.doctor,
            user: appointment.doctor,
            time: getTimeString(appointmentDate, timezone),
          });
        });

        Object.keys(appointmentsByDate).forEach(dateKey => {
          appointmentsByDate[dateKey].sort((a, b) =>
            new Date(a.appointmentDateTime) - new Date(b.appointmentDateTime)
          );
        });

        setAppointments(prev => ({ ...prev, ...appointmentsByDate }));
      } catch (error) {
        loadedMonthsRef.current.delete(monthKey);
        const message = error?.message?.includes('402')
          ? 'La suscripción del usuario está inactiva. Por favor, renueva tu suscripción para acceder a este recurso.'
          : 'Error al cargar las citas';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    if (viewMode === 'month') {
      loadMonth(currentDate.getFullYear(), currentDate.getMonth());
    } else {
      // Week view: load every month that appears in the current week
      const monthsNeeded = new Set();
      for (let i = 0; i < 7; i++) {
        const d = new Date(currentWeekStart);
        d.setDate(currentWeekStart.getDate() + i);
        monthsNeeded.add(`${d.getFullYear()}-${d.getMonth()}`);
      }
      monthsNeeded.forEach(key => {
        const [y, m] = key.split('-').map(Number);
        loadMonth(y, m);
      });
    }
  }, [currentDate, currentWeekStart, viewMode, auth.user?.access_token, timezoneLoading, timezone]);

  useEffect(() => {
    const loadDoctors = async () => {
      if (!auth.user?.access_token) return;

      // Users without agenda:viewall only see their own calendar — no need to load org users
      if (!canViewAll) {
        if (authUser) {
          setDoctors([authUser]);
        }
        return;
      }

      try {
        const users = await UserService.getOrganizationUsers(auth.user.access_token);
        setDoctors(users || []);
      } catch (error) {
        console.error('Error loading doctors:', error);
        try {
          const doctorsResponse = await UserService.getDoctors(auth.user.access_token);
          const doctorsList = doctorsResponse.users || doctorsResponse.data || doctorsResponse;
          if (Array.isArray(doctorsList)) {
            setDoctors(doctorsList);
          }
        } catch (fallbackError) {
          console.error('Error loading doctors fallback:', fallbackError);
        }
      }
    };

    loadDoctors();
  }, [auth.user?.access_token, canViewAll, authUser]);

  useEffect(() => {
    const loadCalendarBlocks = async () => {
      if (!auth.user?.access_token || timezoneLoading || !timezone || !calendarBlockVisibilityInitialized) {
        return;
      }

      if (!showCalendarBlocks) {
        setCalendarBlocks({});
        return;
      }

      if (selectedDoctorIds.length === 0) {
        setCalendarBlocks({});
        return;
      }

      const rangeStart = viewMode === 'month'
        ? DateTime.fromJSDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), { zone: timezone }).startOf('day')
        : DateTime.fromJSDate(currentWeekStart, { zone: timezone }).startOf('day');

      const rangeEnd = viewMode === 'month'
        ? rangeStart.endOf('month')
        : rangeStart.plus({ days: 6 }).endOf('day');

      setIsLoadingBlocks(true);

      try {
        const occurrences = await CalendarBlockService.getCalendarBlockOccurrencesForDoctors(
          selectedDoctorIds,
          {
            startDate: rangeStart.toISODate(),
            endDate: rangeEnd.toISODate(),
            timezone
          },
          auth.user.access_token
        );

        const groupedBlocks = occurrences
          .sort((first, second) => new Date(first.startDateTime) - new Date(second.startDateTime))
          .reduce((accumulator, block) => {
            const dateKey = block.date;
            if (!accumulator[dateKey]) {
              accumulator[dateKey] = [];
            }

            accumulator[dateKey].push(block);
            return accumulator;
          }, {});

        setCalendarBlocks(groupedBlocks);
      } catch (loadError) {
        console.error('Error loading calendar blocks:', loadError);
        setError(loadError?.message || 'Error al cargar los bloqueos');
      } finally {
        setIsLoadingBlocks(false);
      }
    };

    loadCalendarBlocks();
  }, [auth.user?.access_token, calendarBlockVisibilityInitialized, currentDate, currentWeekStart, selectedDoctorIds, showCalendarBlocks, timezone, timezoneLoading, viewMode]);

  // Load locations
  useEffect(() => {
    const loadLocations = async () => {
      if (!auth.user?.access_token) return;

      try {
        const result = await LocationService.getAllLocations(auth.user.access_token);
        const organizationLocations = result.data || [];
        setLocations(organizationLocations);
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };

    loadLocations();
  }, [auth.user?.access_token]);

  useEffect(() => {
    const currentUserId = auth.user?.profile?.sub;
    if (!currentUserId) return;

    const storageKey = `${CALENDAR_SELECTED_DOCTORS_STORAGE_KEY}:${currentUserId}`;

    try {
      const savedValue = localStorage.getItem(storageKey);
      if (savedValue) {
        const parsed = JSON.parse(savedValue);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSelectedDoctorIds(parsed.filter(Boolean));
          setDoctorSelectionInitialized(true);
          return;
        }
      }
    } catch (error) {
      console.error('Error restoring selected calendars:', error);
    }

    setSelectedDoctorIds([currentUserId]);
    setDoctorSelectionInitialized(true);
  }, [auth.user?.profile?.sub]);

  useEffect(() => {
    if (!doctorSelectionInitialized) return;

    const validDoctorIdSet = new Set(
      (doctors || [])
        .map(doctor => doctor.id || doctor.userId)
        .filter(Boolean)
    );

    if (validDoctorIdSet.size === 0) return;

    const currentUserId = auth.user?.profile?.sub;

    setSelectedDoctorIds(prevSelected => {
      const sanitized = prevSelected.filter(id => validDoctorIdSet.has(id));

      if (sanitized.length > 0) {
        return sanitized;
      }

      if (currentUserId && validDoctorIdSet.has(currentUserId)) {
        return [currentUserId];
      }

      return [Array.from(validDoctorIdSet)[0]];
    });
  }, [doctors, auth.user?.profile?.sub, doctorSelectionInitialized]);

  useEffect(() => {
    if (!doctorSelectionInitialized) return;

    const currentUserId = auth.user?.profile?.sub;
    if (!currentUserId) return;

    const storageKey = `${CALENDAR_SELECTED_DOCTORS_STORAGE_KEY}:${currentUserId}`;

    try {
      localStorage.setItem(storageKey, JSON.stringify(selectedDoctorIds));
    } catch (error) {
      console.error('Error saving selected calendars:', error);
    }
  }, [selectedDoctorIds, auth.user?.profile?.sub, doctorSelectionInitialized]);

  // Initialize location selection from localStorage or default to all locations
  useEffect(() => {
    const currentUserId = auth.user?.profile?.sub;
    if (!currentUserId) return;

    const storageKey = `${CALENDAR_SELECTED_LOCATIONS_STORAGE_KEY}:${currentUserId}`;

    try {
      const savedValue = localStorage.getItem(storageKey);
      if (savedValue) {
        const parsed = JSON.parse(savedValue);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSelectedLocationIds(parsed.filter(Boolean));
          setLocationSelectionInitialized(true);
          return;
        }
      }
    } catch (error) {
      console.error('Error restoring selected locations:', error);
    }

    // Default to all locations selected
    setSelectedLocationIds([]);
    setLocationSelectionInitialized(true);
  }, [auth.user?.profile?.sub]);

  // Validate selected locations against available locations
  useEffect(() => {
    if (!locationSelectionInitialized) return;

    const validLocationIdSet = new Set(
      (locations || [])
        .map(location => location.id)
        .filter(Boolean)
    );

    if (validLocationIdSet.size === 0) return;

    setSelectedLocationIds(prevSelected => {
      // If no specific locations selected, select all
      if (prevSelected.length === 0) {
        return Array.from(validLocationIdSet);
      }

      const sanitized = prevSelected.filter(id => validLocationIdSet.has(id));

      if (sanitized.length > 0) {
        return sanitized;
      }

      // Default to all if sanitized is empty
      return Array.from(validLocationIdSet);
    });
  }, [locations, locationSelectionInitialized]);

  // Persist location selection to localStorage
  useEffect(() => {
    if (!locationSelectionInitialized) return;

    const currentUserId = auth.user?.profile?.sub;
    if (!currentUserId) return;

    const storageKey = `${CALENDAR_SELECTED_LOCATIONS_STORAGE_KEY}:${currentUserId}`;

    try {
      localStorage.setItem(storageKey, JSON.stringify(selectedLocationIds));
    } catch (error) {
      console.error('Error saving selected locations:', error);
    }
  }, [selectedLocationIds, auth.user?.profile?.sub, locationSelectionInitialized]);

  useEffect(() => {
    const currentUserId = auth.user?.profile?.sub;
    if (!currentUserId) {
      return;
    }

    const storageKey = `${CALENDAR_SHOW_BLOCKS_STORAGE_KEY}:${currentUserId}`;

    try {
      const savedValue = localStorage.getItem(storageKey);
      if (savedValue !== null) {
        setShowCalendarBlocks(savedValue === 'true');
      }
    } catch (error) {
      console.error('Error restoring calendar block visibility:', error);
    } finally {
      setCalendarBlockVisibilityInitialized(true);
    }
  }, [auth.user?.profile?.sub]);

  useEffect(() => {
    if (!calendarBlockVisibilityInitialized) return;

    const currentUserId = auth.user?.profile?.sub;
    if (!currentUserId) return;

    const storageKey = `${CALENDAR_SHOW_BLOCKS_STORAGE_KEY}:${currentUserId}`;

    try {
      localStorage.setItem(storageKey, String(showCalendarBlocks));
    } catch (error) {
      console.error('Error saving calendar block visibility:', error);
    }
  }, [auth.user?.profile?.sub, calendarBlockVisibilityInitialized, showCalendarBlocks]);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    // Set to first day of month to avoid skipping months when current day > days in next month
    // e.g. Jan 31 -> Feb 31 (invalid) -> Mar 3
    newDate.setDate(1);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
    
    // Set selected date to the first day of the new month
    const firstDayOfMonth = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
    setSelectedDate(firstDayOfMonth);
  };

  const getNavigationTitle = () => {
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  const navigateWeek = (direction) => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(currentWeekStart.getDate() + direction * 7);
    setCurrentWeekStart(newStart);
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(currentWeekStart);
      d.setDate(currentWeekStart.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const getWeekNavigationTitle = () => {
    const days = getWeekDays();
    const first = days[0];
    const last = days[6];
    if (first.getMonth() === last.getMonth()) {
      return `${first.getDate()} – ${last.getDate()} ${monthNames[first.getMonth()]} ${first.getFullYear()}`;
    }
    if (first.getFullYear() === last.getFullYear()) {
      return `${first.getDate()} ${monthNames[first.getMonth()]} – ${last.getDate()} ${monthNames[last.getMonth()]} ${first.getFullYear()}`;
    }
    return `${first.getDate()} ${monthNames[first.getMonth()]} ${first.getFullYear()} – ${last.getDate()} ${monthNames[last.getMonth()]} ${last.getFullYear()}`;
  };

  const switchToWeekView = () => {
    setCurrentWeekStart(getWeekStart(selectedDate));
    setViewMode('week');
  };

  const switchToMonthView = () => {
    setCurrentDate(new Date(currentWeekStart.getFullYear(), currentWeekStart.getMonth(), 1));
    setViewMode('month');
  };

  const getSelectedDayAppointments = () => {
    const dateKey = getDateString(selectedDate, timezone);
    return appointments[dateKey] || [];
  };

  const getAppointmentDoctorId = (appointment) => {
    return (
      appointment.doctorId ||
      appointment.userId ||
      appointment.doctor?.id ||
      appointment.user?.id ||
      null
    );
  };

  const filterAppointmentsBySelectedDoctors = (appointmentList) => {
    if (selectedDoctorIds.length === 0) return [];
    return (appointmentList || []).filter(appointment =>
      selectedDoctorIdSet.has(getAppointmentDoctorId(appointment))
    );
  };

  const filterAppointmentsBySelectedLocations = (appointmentList) => {
    // If no locations configured or all selected, show all
    if (locations.length === 0 || selectedLocationIds.length === 0 || selectedLocationIds.length === locations.length) {
      return appointmentList;
    }
    return (appointmentList || []).filter(appointment =>
      selectedLocationIdSet.has(appointment.locationId)
    );
  };

  const filterAppointmentsBySelectedDoctorsAndLocations = (appointmentList) => {
    // First filter by doctors
    const doctorFiltered = filterAppointmentsBySelectedDoctors(appointmentList);
    // Then filter by locations
    return filterAppointmentsBySelectedLocations(doctorFiltered);
  };

  const getDoctorInitial = (doctor) => {
    const name = (
      doctor?.name ||
      `${doctor?.firstName || ''} ${doctor?.lastName || ''}`.trim() ||
      doctor?.email ||
      ''
    ).trim();

    if (!name) return '?';

    const words = name.split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';

    const first = words[0]?.charAt(0) || '';
    const second = words[1]?.charAt(0) || '';
    return `${first}${second}`.toUpperCase();
  };

  const getDoctorLabel = (doctor) => {
    if (!doctor) return '';
    if (doctor.name) return doctor.name;
    const fullName = `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim();
    return fullName || doctor.email || 'Doctor';
  };

  const getDoctorCalendarColor = (doctor) => {
    if (!doctor) return '#0891b2';
    const doctorId = doctor.id || doctor.userId;
    const currentUserId = auth.user?.profile?.sub;
    const doctorColor =
      doctor.calendarDotColor ||
      doctor.calendarColor ||
      doctor.preferences?.calendarDotColor ||
      null;

    if (doctorId && currentUserId && doctorId === currentUserId) {
      return userPreferences?.calendarDotColor || doctorColor || '#0891b2';
    }

    return doctorColor || '#0891b2';
  };

  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Relative luminance per WCAG formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  const toggleDoctorSelection = (doctorId) => {
    if (!doctorId) return;
    setSelectedDoctorIds(prevSelected => {
      if (prevSelected.includes(doctorId)) {
        if (prevSelected.length === 1) {
          const allDoctorIds = (doctors || [])
            .map(doctor => doctor.id || doctor.userId)
            .filter(Boolean);
          return allDoctorIds.length > 0 ? allDoctorIds : prevSelected;
        }
        return prevSelected.filter(id => id !== doctorId);
      }
      return [...prevSelected, doctorId];
    });
  };

  const renderDoctorAndLocationFilters = () => (
    <>
      <div className="doctor-filter-list">
        {canViewAll && doctors.filter(doctor => doctor.active !== false).map((doctor, index) => {
          const doctorId = doctor.id || doctor.userId;
          const isActive = selectedDoctorIdSet.has(doctorId);
          const doctorColor = getDoctorCalendarColor(doctor);

          return (
            <button
              key={doctorId || doctor.email || `${index}-${getDoctorInitial(doctor)}`}
              type="button"
              className={`doctor-filter-button ${isActive ? 'active' : ''}`}
              onClick={() => toggleDoctorSelection(doctorId)}
              title={getDoctorLabel(doctor)}
              aria-pressed={isActive}
              style={isActive ? { '--doctor-color': doctorColor, color: getContrastColor(doctorColor) } : undefined}
            >
              {getDoctorInitial(doctor)}
            </button>
          );
        })}
      </div>
      {locations.length >= 2 && (
        <div className="location-filter-dropdown">
          <select
            value={selectedLocationIds.length === locations.length ? 'all' : selectedLocationIds[0] || 'all'}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'all') {
                const allLocationIds = locations.map(loc => loc.id).filter(Boolean);
                setSelectedLocationIds(allLocationIds);
              } else {
                setSelectedLocationIds([value]);
              }
            }}
            className="location-dropdown-select"
          >
            <option value="all">Todas las ubicaciones</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <label
        className="blocks-visibility-toggle"
        title={showCalendarBlocks ? 'Ocultar bloqueos' : 'Mostrar bloqueos'}
      >
        <FaLock className="blocks-visibility-icon" aria-hidden="true" />
        <input
          type="checkbox"
          checked={showCalendarBlocks}
          onChange={() => setShowCalendarBlocks((previous) => !previous)}
          aria-label={showCalendarBlocks ? 'Ocultar bloqueos' : 'Mostrar bloqueos'}
        />
        <span className="slider" aria-hidden="true" />
      </label>
    </>
  );

  const handleDayClick = (day) => {
    if (!day) return;
    const newSelectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    setSelectedDate(newSelectedDate);
  };

  const renderMonthView = () => {
    const days = getDaysInMonth();
    
    // Helper function to get unique appointment dots by color (status)
    const getUniqueDotsByColor = (appointmentList) => {
      const colorMap = new Map();
      
      appointmentList.forEach(appointment => {
        const color = getStatusColor(appointment.status);
        // Only add if we haven't already added a dot with this color
        if (!colorMap.has(color)) {
          colorMap.set(color, appointment);
        }
      });
      
      return Array.from(colorMap.values());
    };
    
    return (
      <div className="compact-calendar">
        {/* Day names header */}
        <div className="compact-calendar-header">
          {dayNames.map(day => (
            <div key={day} className="compact-day-name">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="compact-calendar-days">
          {days.map((day, index) => {
            const dayAppointments = getDayAppointments(day);
            const hasAppointments = dayAppointments.length > 0;
            const isSelected = day && isSelectedDay(day);
            const uniqueDots = getUniqueDotsByColor(dayAppointments);
            const holiday = day && isHoliday(day);
            
            return (
              <div 
                key={index} 
                className={`compact-calendar-day ${!day ? 'empty' : ''} ${isToday(day) ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasAppointments ? 'has-appointments' : ''} ${holiday ? 'holiday' : ''}`}
                onClick={() => handleDayClick(day)}
              >
                {day && (
                  <>
                    <span className="compact-day-number">{day}</span>
                    {holiday && (
                      <div 
                        className="holiday-badge"
                        ref={el => badgeRefs.current[day] = el}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipPos({
                            top: rect.top - 36,
                            left: rect.left + rect.width / 2
                          });
                          setHoveredHoliday(day);
                        }}
                        onMouseLeave={() => setHoveredHoliday(null)}
                      >
                        🏛️
                      </div>
                    )}
                    {hasAppointments && (
                      <div className="appointment-dots">
                        {uniqueDots.map((appointment) => (
                          <div 
                            key={appointment.id} 
                            className="appointment-dot"
                            style={{ backgroundColor: getStatusColor(appointment.status) }}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSelectedDayPanel = () => {
    const selectedDayAppointments = getSelectedDayAppointments();
    const selectedDayBlocks = showCalendarBlocks ? getSelectedDayBlocks() : [];
    const selectedDateFormatted = selectedDate.toLocaleDateString('es-ES', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });

    const filteredAppointments = filterAppointmentsBySelectedDoctorsAndLocations(selectedDayAppointments);
    const hasScheduledItems = filteredAppointments.length > 0 || selectedDayBlocks.length > 0;

    return (
      <div className="selected-day-panel">
        <div className="selected-day-header">
          <h3>{selectedDateFormatted}</h3>
          <div className="selected-day-actions">
            {renderDoctorAndLocationFilters()}
          </div>
        </div>
        
        <div className="selected-day-appointments">
          {isLoading || isLoadingBlocks ? (
            <div className="appointments-loading">
              <div className="loading-spinner-small"></div>
            </div>
          ) : hasScheduledItems ? (
            <>
              {selectedDayBlocks.map((block) => (
                <div
                  key={block.id}
                  className="selected-day-appointment selected-day-block"
                  onClick={() => navigate(`/appointments/blocks/${block.doctorId}/${block.blockId}/edit`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="appointment-status-bar block-status-bar"></div>
                  <div className="appointment-content">
                    <div className="appointment-patient-name block-patient-name">
                      <FaLock />
                      {getBlockMonthlySummary(block)}
                    </div>
                    <div className="appointment-doctor">
                      Dr. {getDoctorName(block.doctorId)}
                    </div>
                  </div>
                  <div className="appointment-actions">
                    <button
                      className="appointment-edit-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/appointments/blocks/${block.doctorId}/${block.blockId}/edit`);
                      }}
                    >
                      <FaEdit />
                    </button>
                  </div>
                </div>
              ))}
              {filteredAppointments.map((appointment) => {
              // Debug: Log appointment data to console
              console.log('Appointment data:', {
                id: appointment.id,
                doctor: appointment.doctor,
                user: appointment.user,
                doctorId: appointment.doctorId,
                userId: appointment.userId
              });
              
              return (
              <div 
                key={appointment.id} 
                className="selected-day-appointment"
                onClick={() => handlePatientClick(appointment)}
                style={{ cursor: appointment.patientId ? 'pointer' : 'default' }}
              >
                <div className="appointment-status-bar" style={{ backgroundColor: getStatusColor(appointment.status) }}></div>
                <div className="appointment-content">
                  <div className="appointment-header">
                    <span className="appointment-time">{appointment.time}</span>
                    <span className="appointment-status" style={{ color: getStatusColor(appointment.status) }}>
                      {getStatusText(appointment.status)}
                    </span>
                  </div>
                  <div className="appointment-patient-name">
                    {appointment.patient ? 
                      `${appointment.time} - ${appointment.patient.firstName} ${appointment.patient.lastName}` : 
                      'Paciente no encontrado'
                    }
                  </div>
                  <div className="appointment-doctor">
                    {(appointment.doctor ?
                      `Dr. ${appointment.doctor.name || ''}` :
                      appointment.user ?
                        `Dr. ${appointment.user.name || `${appointment.user.firstName || ''} ${appointment.user.lastName || ''}`.trim()}` :
                        'Sin doctor asignado'
                    )}{appointment.procedure ? ` - ${appointment.procedure}` : ''}
                  </div>
                  {locations.length > 1 && appointment.locationId && (
                    <div className="appointment-location">
                      <FaMapMarkerAlt className="appointment-location-icon" />
                      {locations.find(loc => loc.id === appointment.locationId)?.name}
                    </div>
                  )}
                </div>
                <div className="appointment-actions">
                  {appointment.status !== 'confirmed' && appointment.status !== 'cancelled' && appointment.status !== 'completed' && appointment.status !== 'no-show' && (
                    <button 
                      className="appointment-quick-action-btn confirm-btn"
                      onClick={(e) => handleConfirmAppointment(appointment, e)}
                      title="Confirmar cita"
                    >
                      <FaCheck />
                    </button>
                  )}
                  {appointment.status !== 'cancelled' && appointment.status !== 'completed' && appointment.status !== 'no-show' && (
                    <button 
                      className="appointment-quick-action-btn cancel-btn"
                      onClick={(e) => handleCancelAppointment(appointment, e)}
                      title="Cancelar cita"
                    >
                      <FaTimes />
                    </button>
                  )}
                  <button 
                    className="appointment-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditAppointment(appointment);
                    }}
                  >
                    <FaEdit />
                  </button>
                </div>
              </div>
              );
            })}
            </>
          ) : (
            <div className="no-appointments-selected">
              <p>No hay citas ni bloqueos programados para este día</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getLocationColor = (locationId) => {
    if (!locationId) return null;
    return locations.find(l => l.id === locationId)?.color || null;
  };

  // Greedy column-assignment algorithm for overlapping timed items in week view.
  const computeOverlapLayout = (dayItems) => {
    if (!dayItems.length) return [];

    const withTimes = dayItems
      .map(item => {
        if (typeof item._startMin === 'number' && typeof item._endMin === 'number') {
          return { ...item, _endMin: Math.max(item._endMin, item._startMin + 1) };
        }

        const [h, m] = (item.time || '00:00').split(':').map(Number);
        const startMin = h * 60 + m;
        const endMin = startMin + (item.durationMinutes || 30);
        return { ...item, _startMin: startMin, _endMin: endMin };
      })
      .filter(item => item._endMin > item._startMin);

    withTimes.sort((a, b) => a._startMin - b._startMin || b._endMin - a._endMin);

    const colEndTimes = [];
    withTimes.forEach(item => {
      let col = colEndTimes.findIndex(endTime => endTime <= item._startMin);
      if (col === -1) {
        col = colEndTimes.length;
        colEndTimes.push(item._endMin);
      } else {
        colEndTimes[col] = item._endMin;
      }
      item._col = col;
    });

    return withTimes.map(item => {
      const overlapping = withTimes.filter(
        other => item._startMin < other._endMin && item._endMin > other._startMin
      );
      const numCols = Math.max(...overlapping.map(other => other._col)) + 1;
      return { ...item, col: item._col, numCols };
    });
  };

  const handleWeekDayClick = (day) => {
    const year = day.getFullYear();
    const month = (day.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = day.getDate().toString().padStart(2, '0');
    navigate(`/appointments/new?date=${year}-${month}-${dayStr}`);
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays();
    const today = new Date();
    const HOUR_HEIGHT = 64;

    const startH = parseInt((userPreferences?.dayViewStartTime || '08:00').split(':')[0], 10);
    const endH = parseInt((userPreferences?.dayViewEndTime || '19:00').split(':')[0], 10);
    const viewStartMin = startH * 60;
    const viewEndMin = endH * 60;
    const totalHours = endH - startH;

    const hours = [];
    for (let h = startH; h <= endH; h++) hours.push(h);

    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const showNowLine = now.toDateString() !== today.toDateString()
      ? false
      : nowMin >= viewStartMin && nowMin < viewEndMin;
    const nowTop = ((nowMin - viewStartMin) / 60) * HOUR_HEIGHT;
    const shouldShowWeekFilters = canViewAll || locations.length >= 2;

    return (
      <div className="week-view">
        {shouldShowWeekFilters && (
          <div className="week-filters-subheader">
            <div className="week-filters-controls">
              {renderDoctorAndLocationFilters()}
            </div>
          </div>
        )}

        {/* Sticky day header */}
        <div className="week-header">
          <div className="week-gutter-spacer" />
          {weekDays.map((day, i) => {
            const isCurrentDay = day.toDateString() === today.toDateString();
            const dateKey = getDateString(day, timezone);
            const allDayBlocks = showCalendarBlocks
              ? (calendarBlocks[dateKey] || []).filter((block) => block.allDay)
              : [];
            return (
              <div key={i} className={`week-day-header-cell${isCurrentDay ? ' today' : ''}`}>
                <span className="week-day-name">{dayNames[i]}</span>
                <span className={`week-day-number${isCurrentDay ? ' today' : ''}`}>
                  {day.getDate()}
                </span>
                {allDayBlocks.length > 0 && (
                  <div className="week-day-all-day-list">
                    {allDayBlocks.map((block) => (
                      <button
                        key={block.id}
                        type="button"
                        className="week-day-all-day-block"
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/appointments/blocks/${block.doctorId}/${block.blockId}/edit`);
                        }}
                        title={[
                          getBlockPrimaryLabel(block),
                          `Doctor: ${getDoctorName(block.doctorId)}`,
                          'Todo el día'
                        ].join('\n')}
                      >
                        <FaLock />
                        <span>Dr. {getDoctorName(block.doctorId)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Scrollable body */}
        <div className="week-body">
          {/* Time gutter */}
          <div className="week-time-gutter">
            {hours.map((h, idx) => (
              <div key={h} className="week-hour-label" style={{ height: HOUR_HEIGHT }}>
                {idx > 0 ? `${h.toString().padStart(2, '0')}:00` : ''}
              </div>
            ))}
          </div>

          {/* Day columns area */}
          <div
            className="week-columns-area"
            style={{ height: totalHours * HOUR_HEIGHT }}
          >
            {/* Current time indicator */}
            {showNowLine && (
              <div className="week-now-line" style={{ top: nowTop }}>
                <div className="week-now-dot" />
              </div>
            )}

            {/* Day columns */}
            {weekDays.map((day, dayIndex) => {
              const dateKey = getDateString(day, timezone);
              const dayBlocks = showCalendarBlocks
                ? (calendarBlocks[dateKey] || []).filter((block) => !block.allDay)
                : [];
              const timedBlocks = dayBlocks
                .map((block) => {
                  const start = DateTime.fromISO(block.startDateTime, { setZone: true }).setZone(timezone);
                  const end = DateTime.fromISO(block.endDateTime, { setZone: true }).setZone(timezone);

                  if (!start.isValid || !end.isValid) {
                    return null;
                  }

                  const blockStartMin = start.hour * 60 + start.minute;
                  const blockEndMin = end.hasSame(start, 'day') ? (end.hour * 60 + end.minute) : 24 * 60;

                  return {
                    ...block,
                    _itemType: 'block',
                    _layoutKey: `block-${block.id}-${block.startDateTime}`,
                    _startMin: blockStartMin,
                    _endMin: blockEndMin,
                  };
                })
                .filter(Boolean);
              const dayAppts = filterAppointmentsBySelectedDoctorsAndLocations(
                appointments[dateKey] || []
              ).map((appointment) => ({
                ...appointment,
                _itemType: 'appointment',
                _layoutKey: `appointment-${appointment.id}`,
              }));
              const laidOut = computeOverlapLayout([...timedBlocks, ...dayAppts]);
              const isCurrentDay = day.toDateString() === today.toDateString();

              return (
                <div
                  key={dayIndex}
                  className={`week-day-column${isCurrentDay ? ' today' : ''}`}
                  onClick={() => handleWeekDayClick(day)}
                >
                  {laidOut.map((item) => {
                    if (item._endMin <= viewStartMin || item._startMin >= viewEndMin) {
                      return null;
                    }

                    const clippedStartMin = Math.max(item._startMin, viewStartMin);
                    const clippedEndMin = Math.min(item._endMin, viewEndMin);

                    if (clippedEndMin <= clippedStartMin) {
                      return null;
                    }

                    const topPx = Math.max(0, ((clippedStartMin - viewStartMin) / 60) * HOUR_HEIGHT);
                    const heightPx = Math.max(22, ((clippedEndMin - clippedStartMin) / 60) * HOUR_HEIGHT);
                    const widthPct = 100 / item.numCols;
                    const leftPct = (item.col / item.numCols) * 100;

                    if (item._itemType === 'block') {
                      const blockTooltip = [
                        `Bloqueado: ${item.allDay ? 'Todo el día' : getBlockTimeLabel(item)}`,
                        `Doctor: ${getDoctorName(item.doctorId)}`,
                        `Motivo: ${item.reason || 'Sin motivo'}`
                      ].join('\n');

                      return (
                        <div
                          key={item._layoutKey}
                          className={`week-calendar-block${item.allDay ? ' all-day' : ''}`}
                          style={{
                            top: topPx,
                            height: heightPx,
                            left: `calc(${leftPct}% + 2px)`,
                            width: `calc(${widthPct}% - 4px)`
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/appointments/blocks/${item.doctorId}/${item.blockId}/edit`);
                          }}
                          title={blockTooltip}
                        >
                          <span className="week-block-main">
                            <FaLock />
                            Dr. {getDoctorName(item.doctorId)}
                          </span>
                          {heightPx > 36 && (
                            <span className="week-block-time">{getBlockTimeLabel(item)}</span>
                          )}
                          {heightPx > 54 && (
                            <span className="week-block-doctor">{item.reason || 'Sin motivo'}</span>
                          )}
                        </div>
                      );
                    }

                    const bgColor = getLocationColor(item.locationId) || getStatusColor(item.status);
                    const txtColor = getContrastColor(bgColor);
                    const patientName = item.patient
                      ? `${item.patient.firstName} ${item.patient.lastName}`
                      : 'Sin paciente';
                    const doctorName = item.doctor?.name
                      || `${item.doctor?.firstName || ''} ${item.doctor?.lastName || ''}`.trim()
                      || item.user?.name
                      || `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.trim()
                      || 'Sin doctor asignado';
                    const locationName = locations.find(loc => loc.id === item.locationId)?.name || 'Sin ubicación';
                    const appointmentTooltip = [
                      `Hora: ${item.time || 'Sin hora'}`,
                      `Paciente: ${patientName}`,
                      `Doctor: ${doctorName}`,
                      `Ubicación: ${locationName}`
                    ].join('\n');

                    return (
                      <div
                        key={item._layoutKey}
                        className={`week-appointment${item.status === 'cancelled' ? ' cancelled' : ''}`}
                        style={{
                          top: topPx,
                          height: heightPx,
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                          backgroundColor: bgColor,
                          color: txtColor,
                          opacity: item.status === 'cancelled' ? 0.55 : 1,
                        }}
                        onClick={event => {
                          event.stopPropagation();
                          handleEditAppointment(item);
                        }}
                        title={appointmentTooltip}
                      >
                        <span className="week-appt-main">{`${item.time} · ${patientName}`}</span>
                        {item.procedure && heightPx > 44 && (
                          <span className="week-appt-procedure">{item.procedure}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const getDateKey = (day) => {
    if (!day) return null;
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    return `${year}-${month}-${dayStr}`;
  };

  const getDayAppointments = (day) => {
    const dateKey = getDateKey(day);
    return filterAppointmentsBySelectedDoctorsAndLocations(appointments[dateKey] || []);
  };

  const getSelectedDayBlocks = () => {
    const dateKey = getDateString(selectedDate, timezone);
    return showCalendarBlocks ? (calendarBlocks[dateKey] || []) : [];
  };

  const getDoctorName = (doctorId) => {
    const doctor = doctors.find((item) => (item.id || item.userId) === doctorId);
    return doctor?.fullName || doctor?.name || doctor?.username || doctor?.email || 'Doctor';
  };

  const getBlockTimeLabel = (block) => {
    if (block.allDay) {
      return 'Todo el día';
    }

    const start = DateTime.fromISO(block.startDateTime, { setZone: true }).setZone(timezone);
    const end = DateTime.fromISO(block.endDateTime, { setZone: true }).setZone(timezone);

    if (!start.isValid || !end.isValid) {
      return block.startTime || 'Horario bloqueado';
    }

    return `${start.toFormat('HH:mm')} - ${end.toFormat('HH:mm')}`;
  };

  const getBlockPrimaryLabel = (block) => {
    if (block.reason) {
      return block.reason;
    }

    return `Bloqueo de ${getBlockTimeLabel(block)}`;
  };

  const getBlockMonthlySummary = (block) => {
    if (block.reason) {
      return `${getBlockTimeLabel(block)} - ${block.reason}`;
    }

    return getBlockPrimaryLabel(block);
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelectedDay = (day) => {
    if (!day) return false;
    return (
      day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isHoliday = (day) => {
    if (!day) return null;
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    const dateKey = `${year}-${month}-${dayStr}`;
    return MEXICAN_HOLIDAYS_2026[dateKey] || null;
  };

  const handleAddAppointment = (day) => {
    // Navigate to full-screen new appointment page with selected date
    const baseDate = typeof day === 'number'
      ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      : selectedDate;
    const year = baseDate.getFullYear();
    const month = (baseDate.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = baseDate.getDate().toString().padStart(2, '0');
    const dateString = `${year}-${month}-${dayStr}`;
    navigate(`/appointments/new?date=${dateString}`);
  };

  const handlePatientClick = (appointment) => {
    if (appointment.patientId) {
      navigate(`/patients/${appointment.patientId}`);
    }
  };

  const handleEditAppointment = (appointment) => {
    // Navigate to full-screen edit appointment page
    navigate(`/appointments/edit/${appointment.id}`);
  };

  const handleConfirmAppointment = async (appointment, event) => {
    event.stopPropagation(); // Prevent triggering the edit action
    
    try {
      await AppointmentService.updateAppointment(
        appointment.id,
        { status: 'confirmed' },
        auth.user.access_token
      );
      
      // Update the appointment in the local state
      setAppointments(prevAppointments => {
        const dateKey = getDateString(selectedDate, timezone);
        const appointmentList = prevAppointments[dateKey] || [];
        const updatedList = appointmentList.map(apt => 
          apt.id === appointment.id ? { ...apt, status: 'confirmed' } : apt
        );
        
        return {
          ...prevAppointments,
          [dateKey]: updatedList
        };
      });
    } catch (error) {
      console.error('Error confirming appointment:', error);
      setError('Error al confirmar la cita');
    }
  };

  const handleCancelAppointment = async (appointment, event) => {
    event.stopPropagation(); // Prevent triggering the edit action
    
    try {
      await AppointmentService.updateAppointment(
        appointment.id,
        { status: 'cancelled' },
        auth.user.access_token
      );
      
      // Update the appointment in the local state
      setAppointments(prevAppointments => {
        const dateKey = getDateString(selectedDate, timezone);
        const appointmentList = prevAppointments[dateKey] || [];
        const updatedList = appointmentList.map(apt => 
          apt.id === appointment.id ? { ...apt, status: 'cancelled' } : apt
        );
        
        return {
          ...prevAppointments,
          [dateKey]: updatedList
        };
      });
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      setError('Error al cancelar la cita');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#28a745';
      case 'in-progress': return '#007bff';
      case 'completed': return '#6c757d';
      case 'cancelled': return '#dc3545';
      case 'no-show': return '#fd7e14';
      default: return '#ffc107'; // scheduled
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'scheduled': return 'Programada';
      case 'confirmed': return 'Confirmada';
      case 'in-progress': return 'En Progreso';
      case 'completed': return 'Completada';
      case 'cancelled': return 'Cancelada';
      case 'no-show': return 'No Asistió';
      default: return status;
    }
  };

  const isCalendarLoading = isLoading || isLoadingBlocks;

  return (
    <div className="appointments-container">
      <div className="appointments-header">
        <div className="header-content">
          <h1>Citas</h1>
          <p>Gestión de citas y calendario</p>
        </div>
        <div className="appointments-header-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleAddAppointment()}
            disabled={isCalendarLoading}
          >
            <FaPlus />
            Agregar cita
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/appointments/blocks')}
          >
            <FaLock />
            Bloqueos
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="calendar-container">
        {/* Calendar Navigation */}
        <div className="calendar-header">
          <div className="calendar-nav-group">
            <button
              type="button"
              className="nav-button"
              onClick={() => viewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)}
              disabled={isCalendarLoading}
              aria-label="Ir al periodo anterior"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              className="nav-button"
              onClick={() => viewMode === 'month' ? navigateMonth(1) : navigateWeek(1)}
              disabled={isCalendarLoading}
              aria-label="Ir al siguiente periodo"
            >
              <FaChevronRight />
            </button>
          </div>

          <h2 className="month-year">
            {viewMode === 'month' ? getNavigationTitle() : getWeekNavigationTitle()}
          </h2>

          <div className="calendar-view-switch">
            <select
              className="view-mode-select"
              value={viewMode}
              onChange={(event) => {
                if (event.target.value === 'month') {
                  switchToMonthView();
                  return;
                }
                switchToWeekView();
              }}
              disabled={isCalendarLoading}
              aria-label="Cambiar vista del calendario"
            >
              <option value="month">Mes</option>
              <option value="week">Semana</option>
            </select>
          </div>
        </div>

        {/* Main Calendar Layout */}
        {viewMode === 'month' ? (
          <div className="calendar-layout">
            <div className="calendar-section">
              {isCalendarLoading ? (
                <div className="calendar-loading">
                  <div className="loading-spinner"></div>
                </div>
              ) : (
                renderMonthView()
              )}
            </div>
            <div className="appointments-section">
              {renderSelectedDayPanel()}
            </div>
          </div>
        ) : (
          <div className="calendar-layout week-mode">
            {isCalendarLoading ? (
              <div className="calendar-loading">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              renderWeekView()
            )}
          </div>
        )}
      </div>

      {hoveredHoliday && tooltipPos && (
        <div 
          className="holiday-tooltip"
          style={{
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
            transform: 'translateX(-50%)'
          }}
        >
          {isHoliday(hoveredHoliday)}
        </div>
      )}
    </div>
  );
};

export default Appointments;
